/**
 * Content TOC Navigation Component
 * H2見出しを自動検出してナビゲーションを動的生成
 */
class ContentTocNavigation extends HTMLElement {
  constructor() {
    super();
    this.list = null;
    this.emptyMessage = null;
    this.headings = [];
  }

  connectedCallback() {
    this.list = this.querySelector('[data-toc-list]');
    this.emptyMessage = this.querySelector('[data-toc-empty]');

    // DOM完全読み込み後に実行
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // 少し遅延させてコンテンツが確実に読み込まれるようにする
      requestAnimationFrame(() => this.init());
    }
  }

  get contentSelector() {
    return this.dataset.contentSelector || '.rte';
  }

  get headingLevel() {
    return this.dataset.headingLevel || 'h2';
  }

  get minHeadings() {
    return parseInt(this.dataset.minHeadings, 10) || 2;
  }

  get sectionId() {
    return this.dataset.wethemeSectionId || '';
  }

  get buttonsStyle() {
    return this.dataset.buttonsStyle || 'outlined';
  }

  init() {
    this.findHeadings();

    if (this.headings.length >= this.minHeadings) {
      this.assignHeadingIds();
      this.buildNavigation();
      this.attachEvents();
      this.show();
    } else {
      this.handleEmpty();
    }
  }

  findHeadings() {
    const contentArea = document.querySelector(this.contentSelector);
    if (!contentArea) {
      // フォールバック: .rte を探す
      const fallbackArea = document.querySelector('.rte');
      if (fallbackArea) {
        this.headings = Array.from(fallbackArea.querySelectorAll(this.headingLevel));
      }
      return;
    }

    this.headings = Array.from(contentArea.querySelectorAll(this.headingLevel));
  }

  assignHeadingIds() {
    this.headings.forEach((heading, index) => {
      if (!heading.id) {
        const text = heading.textContent.trim();
        const baseId = this.generateId(text, index);
        heading.id = baseId;
      }
    });
  }

  generateId(text, index) {
    // 英数字、日本語（ひらがな、カタカナ、漢字）、ハイフンを許可
    let id = text
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\-]/g, '')
      .substring(0, 50);

    // 空の場合はインデックスベース
    if (!id) {
      id = `toc-section-${index + 1}`;
    }

    // 重複回避
    let finalId = id;
    let counter = 1;
    while (document.getElementById(finalId) && document.getElementById(finalId) !== this.headings[index]) {
      finalId = `${id}-${counter}`;
      counter++;
    }

    return finalId;
  }

  buildNavigation() {
    if (!this.list) return;

    const fragment = document.createDocumentFragment();
    const styleClasses = this.getButtonStyleClasses();

    this.headings.forEach((heading, index) => {
      const li = document.createElement('li');
      li.className = styleClasses.liClass;

      // アニメーション属性（10個以下の場合）
      if (this.headings.length <= 10) {
        li.setAttribute('data-animate', '');
        li.setAttribute('data-animate-order', String(index + 2));
      }

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.className = `dynamic-section-button--${this.sectionId} ${styleClasses.btnClass}`;
      link.textContent = heading.textContent.trim();
      link.setAttribute('data-toc-link', '');

      li.appendChild(link);
      fragment.appendChild(li);
    });

    this.list.appendChild(fragment);
  }

  getButtonStyleClasses() {
    const styleMap = {
      filled: { btnClass: 'content-toc-navigation__button btn', liClass: '' },
      outlined: { btnClass: 'content-toc-navigation__button btn--secondary', liClass: '' },
      underlined: { btnClass: 'content-toc-navigation__button btn--underlined', liClass: 'text-link-animated' }
    };

    return styleMap[this.buttonsStyle] || styleMap.outlined;
  }

  attachEvents() {
    if (!this.list) return;

    this.list.addEventListener('click', (e) => {
      const link = e.target.closest('[data-toc-link]');
      if (link) {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const target = document.getElementById(targetId);
        if (target) {
          this.smoothScrollTo(target);
          // URLハッシュを更新
          history.pushState(null, '', `#${targetId}`);
        }
      }
    });
  }

  smoothScrollTo(element) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = prefersReducedMotion.matches ? 'auto' : 'smooth';

    // ヘッダーオフセットを考慮
    const headerOffset = this.getHeaderOffset();
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: behavior
    });

    // フォーカス管理（アクセシビリティ）
    element.setAttribute('tabindex', '-1');
    element.focus({ preventScroll: true });
  }

  getHeaderOffset() {
    // 固定ヘッダーがある場合のオフセット計算
    const stickyHeader = document.querySelector('.site-header--sticky, [data-sticky-header], .header-sticky');
    if (stickyHeader) {
      return stickyHeader.offsetHeight + 20;
    }
    return 20;
  }

  show() {
    this.hidden = false;
    if (this.emptyMessage) {
      this.emptyMessage.hidden = true;
    }
  }

  handleEmpty() {
    const emptyText = this.emptyMessage?.textContent?.trim();
    if (emptyText) {
      this.emptyMessage.hidden = false;
    } else {
      // メッセージがない場合はセクション全体を非表示
      this.hidden = true;
    }
  }
}

customElements.define('content-toc-navigation', ContentTocNavigation);
